<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion :key="accordionStructureKey" v-model="value" multiple>
      <VaCollapse v-for="route in roleRoutes" :key="route.name">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${route.displayName}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ route.displayName }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="childRoute in route.children" :key="childRoute.name">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ childRoute.displayName }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed, Ref } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import roleCapacitiesManager, {
  UserRoles,
  ExternalSpec,
  ArmadorType,
  UserRole,
} from '../../managers/identity/RoleCapacitiesManager'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const userRole: Ref<UserRole | null> = roleCapacitiesManager.getRefUserRole()
    const rolesChecker = (roles: Array<UserRoles | ExternalSpec | ArmadorType>) => {
      return roles.some((role) => {
        if (!userRole.value) return false
        if (role == ArmadorType.DIRECTO)
          return (
            userRole.value.role == UserRoles.EXTERNO &&
            userRole.value.externalSpec == ExternalSpec.ARMADOR &&
            // Armador natural directo: ve el menú "Apoderados" (administra sus propios apoderados).
            // Cuando entra como representante, solo el Representante LEGAL de un armador jurídico
            // lo ve (es el "dueño" de la empresa); los apoderados de un jurídico no.
            (!userRole.value.isRepresentante ||
              (userRole.value.armadorIsJuridica === true && roleCapacitiesManager.isActingAsRepresentanteLegal()))
          )
        if (role == ArmadorType.JURIDICA)
          return (
            userRole.value.role == UserRoles.EXTERNO &&
            userRole.value.externalSpec == ExternalSpec.ARMADOR &&
            userRole.value.armadorIsJuridica === true
          )
        if (role == ArmadorType.ESTE || role == ArmadorType.WESTE)
          return (
            userRole.value.role == UserRoles.EXTERNO &&
            userRole.value.externalSpec == ExternalSpec.ARMADOR &&
            role == userRole.value.armadorType
          )
        if (role == ExternalSpec.PROVEEDOR || role == ExternalSpec.ARMADOR || role == ExternalSpec.TRIPULANTE)
          return userRole.value.role == UserRoles.EXTERNO && userRole.value.externalSpec == role
        else return role == userRole.value.role
      })
    }

    const roleRoutes = computed(
      () =>
        navigationRoutes.routes
          .map((route: INavigationRoute) => {
            const allowedChildren = route.children
              ? route.children.filter((child: INavigationRoute) => {
                  if (!child.roles || child.roles.length == 0) return true
                  return rolesChecker(child.roles)
                })
              : []
            if (allowedChildren.length > 0) {
              return { ...route, children: allowedChildren }
            } else {
              if (route.children && route.children.length > 0) return null
              else if (!route.roles || route.roles.length == 0) return null
              else if (rolesChecker(route.roles)) return route
            }
            return null
          })
          .filter((route) => route !== null) as INavigationRoute[],
    )

    // Firma estructural de la barra: cambia solo cuando aparece/desaparece o se reordena
    // una sección o sus hijos (p. ej. al resolverse async armadorIsJuridica / isRepresentante).
    // Se usa como :key del VaAccordion para forzar un re-montaje limpio en esos casos: así el
    // registro interno de items del acordeón (indexado por orden de montaje y acoplado al
    // v-model posicional) se reconstruye alineado con la lista visible. Sin esto, reutilizar
    // instancias con índices desfasados deja items "no registrados" y el menú no abre
    // ("Accordion item is not registered yet" / "Could not find item").
    const accordionStructureKey = computed(() =>
      roleRoutes.value.map((r) => `${r.name}:${r.children?.map((c) => c.name).join(',') ?? ''}`).join('|'),
    )

    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`)
      }

      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    const setActiveExpand = () => {
      const newValue = roleRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route))
      const hasActiveRoute = newValue.some((v) => v)
      const sizeChanged = newValue.length !== value.value.length
      // Solo sobrescribir si hay una ruta activa para resaltar, o si cambió la cantidad de rutas.
      // Si no hay ruta activa y el tamaño es el mismo, preservar el estado manual del usuario.
      if (hasActiveRoute || sizeChanged) {
        value.value = newValue
      }
    }

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })
    watch(roleRoutes, setActiveExpand)

    return {
      writableVisible,
      sidebarWidth,
      value,
      accordionStructureKey,
      color,
      activeColor,
      navigationRoutes,
      roleRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
